import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export type ServiceKey =
  | "tmdb"
  | "ai"
  | "payments_polar"
  | "payments_paypal"
  | "email";

export type ServiceStatus = "operational" | "degraded" | "down";

export interface ServiceHealthRow {
  service: ServiceKey;
  label: string;
  status: ServiceStatus;
  reason: string | null;
  manual_override: "operational" | "down" | null;
  last_failure_at: string | null;
  last_recovered_at: string | null;
  updated_at: string;
}

export const SERVICE_LABELS: Record<ServiceKey, string> = {
  tmdb: "Movie & TV catalogue",
  ai: "Smart discovery",
  payments_polar: "Card checkout",
  payments_paypal: "PayPal checkout",
  email: "Email delivery",
};

export function effectiveStatus(row?: ServiceHealthRow | null): ServiceStatus {
  if (!row) return "operational";
  if (row.manual_override === "down") return "down";
  if (row.manual_override === "operational") return "operational";
  return row.status;
}

/**
 * Live view of which external services are healthy. Read-only for members and
 * guests; kept fresh over realtime so a recovery re-opens features without a reload.
 */
export const useServiceHealth = () => {
  const [rows, setRows] = useState<ServiceHealthRow[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchRows = useCallback(async () => {
    const { data, error } = await supabase
      .from("service_health")
      .select("service, label, status, reason, manual_override, last_failure_at, last_recovered_at, updated_at");
    if (!error && data) setRows(data as unknown as ServiceHealthRow[]);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchRows();
    const channel = supabase
      .channel("service-health")
      .on("postgres_changes", { event: "*", schema: "public", table: "service_health" }, () => {
        fetchRows();
      })
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchRows]);

  const statusOf = useCallback(
    (service: ServiceKey) => effectiveStatus(rows.find((r) => r.service === service)),
    [rows]
  );

  const isDown = useCallback((service: ServiceKey) => statusOf(service) === "down", [statusOf]);

  return { rows, loading, statusOf, isDown, refetch: fetchRows };
};

/** Convenience hook for a single service. */
export const useServiceDown = (service: ServiceKey) => {
  const { isDown, loading, rows } = useServiceHealth();
  const row = rows.find((r) => r.service === service);
  return { isDown: isDown(service), reason: row?.reason ?? null, loading };
};

export default useServiceHealth;
